import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'

function formatPhone(raw) {
  var d = (raw || '').replace(/\D/g, '').slice(0, 10)
  if (d.length === 10) return '(' + d.slice(0, 3) + ') ' + d.slice(3, 6) + '-' + d.slice(6)
  return raw || ''
}

function timeAgo(iso) {
  if (!iso) return ''
  var ms = Date.now() - new Date(iso).getTime()
  if (ms < 60000) return 'just now'
  if (ms < 3600000) return Math.floor(ms / 60000) + ' min ago'
  if (ms < 86400000) return Math.floor(ms / 3600000) + ' hr ago'
  return Math.floor(ms / 86400000) + ' days ago'
}

export default function StoreQuestionsPage() {
  var { user } = useAuth()
  var navigate = useNavigate()
  var [searchParams] = useSearchParams()
  var highlightedId = searchParams.get('q') || null
  var [questions, setQuestions] = useState([])
  var [loading, setLoading] = useState(true)
  var [filter, setFilter] = useState('all') // all | urgent | new | contacted | resolved
  var [busyId, setBusyId] = useState(null)
  var [notesEdit, setNotesEdit] = useState(null) // { id, draft }

  var fetchQuestions = useCallback(async function() {
    setLoading(true)
    var { data, error } = await supabase.from('store_quick_questions').select('*')
      .order('urgency', { ascending: false }) // 'urgent' < 'normal' alphabetically — flip with secondary sort below
      .order('created_at', { ascending: false })
    if (error) { console.error('[StoreQuestionsPage] fetch failed', error); setLoading(false); return }
    // Sort: open urgent first, then new, then contacted, then resolved/closed; within group newest first
    var rows = (data || []).slice().sort(function(a, b) {
      var aOpen = a.status !== 'resolved' && a.status !== 'closed'
      var bOpen = b.status !== 'resolved' && b.status !== 'closed'
      if (aOpen !== bOpen) return aOpen ? -1 : 1
      var aUrgent = a.urgency === 'urgent' && aOpen
      var bUrgent = b.urgency === 'urgent' && bOpen
      if (aUrgent !== bUrgent) return aUrgent ? -1 : 1
      return new Date(b.created_at) - new Date(a.created_at)
    })
    setQuestions(rows)
    setLoading(false)
  }, [])

  useEffect(function() { fetchQuestions() }, [fetchQuestions])

  var counts = {
    all: questions.length,
    urgent: questions.filter(function(q) { return q.urgency === 'urgent' && q.status !== 'resolved' && q.status !== 'closed' }).length,
    new: questions.filter(function(q) { return q.status === 'new' }).length,
    contacted: questions.filter(function(q) { return q.status === 'contacted' }).length,
    resolved: questions.filter(function(q) { return q.status === 'resolved' || q.status === 'closed' }).length,
  }
  var visible = questions.filter(function(q) {
    if (filter === 'all') return true
    if (filter === 'urgent') return q.urgency === 'urgent' && q.status !== 'resolved' && q.status !== 'closed'
    if (filter === 'new') return q.status === 'new'
    if (filter === 'contacted') return q.status === 'contacted'
    if (filter === 'resolved') return q.status === 'resolved' || q.status === 'closed'
    return true
  })

  var markCalled = async function(q) {
    setBusyId(q.id)
    var nowIso = new Date().toISOString()
    var { error } = await supabase.from('store_quick_questions')
      .update({ status: 'contacted', last_contact_at: nowIso, updated_at: nowIso })
      .eq('id', q.id)
    if (error) { alert('Could not mark called: ' + error.message); setBusyId(null); return }
    setQuestions(function(prev) { return prev.map(function(r) { return r.id === q.id ? Object.assign({}, r, { status: 'contacted', last_contact_at: nowIso }) : r }) })
    setBusyId(null)
  }

  var markResolved = async function(q) {
    setBusyId(q.id)
    var nowIso = new Date().toISOString()
    var { error } = await supabase.from('store_quick_questions')
      .update({ status: 'resolved', resolved_at: nowIso, resolved_by: user.id, updated_at: nowIso })
      .eq('id', q.id)
    if (error) { alert('Could not resolve: ' + error.message); setBusyId(null); return }
    setQuestions(function(prev) { return prev.map(function(r) { return r.id === q.id ? Object.assign({}, r, { status: 'resolved', resolved_at: nowIso, resolved_by: user.id }) : r }) })
    setBusyId(null)
  }

  var saveNotes = async function() {
    if (!notesEdit) return
    setBusyId(notesEdit.id)
    var { error } = await supabase.from('store_quick_questions')
      .update({ owner_notes: notesEdit.draft || null, updated_at: new Date().toISOString() })
      .eq('id', notesEdit.id)
    if (error) { alert('Could not save notes: ' + error.message); setBusyId(null); return }
    setQuestions(function(prev) { return prev.map(function(r) { return r.id === notesEdit.id ? Object.assign({}, r, { owner_notes: notesEdit.draft || null }) : r }) })
    setNotesEdit(null)
    setBusyId(null)
  }

  var goToBid = function(q) {
    // Hand off to BidsPage with the question id so a future enhancement can
    // pre-fill from the store_quick_questions row. Today the owner manually
    // pulls the customer info from the question card while creating the bid.
    navigate('/bids?fromQuestion=' + q.id)
  }

  return (
    <div style={st.container}>
      <div style={st.header}>
        <div>
          <div style={st.title}>QUICK QUESTIONS</div>
          <div style={st.sub}>{counts.urgent > 0 && <span style={{ color: '#e05c5c', fontWeight: 600 }}>{counts.urgent} URGENT </span>}{String.fromCodePoint(0x00B7)} {counts.all} total</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '20px' }}>
        {[
          { id: 'all', label: 'All' },
          { id: 'urgent', label: 'Urgent' },
          { id: 'new', label: 'New' },
          { id: 'contacted', label: 'Contacted' },
          { id: 'resolved', label: 'Resolved' },
        ].map(function(f) {
          var active = filter === f.id
          var c = counts[f.id]
          return <button key={f.id} type="button" onClick={function() { setFilter(f.id) }}
            style={Object.assign({}, st.chip, active ? st.chipActive : {}, f.id === 'urgent' && c > 0 ? { borderColor: 'rgba(224,92,92,0.4)', color: '#e05c5c' } : {})}>
            {f.label}{c > 0 ? ' (' + c + ')' : ''}
          </button>
        })}
      </div>

      {loading ? (
        <div style={st.empty}>Loading\u2026</div>
      ) : visible.length === 0 ? (
        <div style={st.empty}>No questions match this filter.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {visible.map(function(q) {
            var isUrgent = q.urgency === 'urgent' && q.status !== 'resolved' && q.status !== 'closed'
            var isHighlighted = highlightedId === q.id
            var statusColor = q.status === 'resolved' ? '#4a8c6a' : q.status === 'contacted' ? '#4a6a8c' : q.status === 'closed' ? 'rgba(255,255,255,0.4)' : '#b48c3c'
            return (
              <div key={q.id} style={Object.assign({}, st.card, isUrgent ? st.cardUrgent : {}, isHighlighted ? { boxShadow: '0 0 0 2px rgba(180,140,60,0.6)' } : {})}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      {isUrgent && <span style={st.urgentBadge}>{String.fromCodePoint(0x1F6A8)} URGENT</span>}
                      <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>{timeAgo(q.created_at)}</span>
                    </div>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.45)', marginBottom: '6px' }}>From: {q.store_name || 'Store Partner'}</div>
                    <div style={{ fontSize: '15px', color: '#ffffff', fontWeight: 600 }}>{q.client_name || '(no name)'}</div>
                    <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginTop: '6px' }}>
                      {q.client_phone && (
                        <a href={'tel:' + q.client_phone} style={st.contactLink}>
                          {String.fromCodePoint(0x1F4DE)} {formatPhone(q.client_phone)}
                        </a>
                      )}
                      {q.client_email && (
                        <a href={'mailto:' + q.client_email} style={st.contactLink}>
                          {String.fromCodePoint(0x2709, 0xFE0F)} {q.client_email}
                        </a>
                      )}
                    </div>
                    {q.question && (
                      <div style={st.questionBox}>&ldquo;{q.question}&rdquo;</div>
                    )}
                    {q.owner_notes && (
                      <div style={st.notesBox}>
                        <div style={st.notesLabel}>NOTES</div>
                        <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', whiteSpace: 'pre-wrap' }}>{q.owner_notes}</div>
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={Object.assign({}, st.statusBadge, { color: statusColor, borderColor: statusColor + '60' })}>{(q.status || 'new').toUpperCase()}</div>
                  </div>
                </div>

                {notesEdit && notesEdit.id === q.id ? (
                  <div style={{ marginTop: '12px' }}>
                    <textarea
                      value={notesEdit.draft}
                      onChange={function(e) { setNotesEdit({ id: q.id, draft: e.target.value }) }}
                      placeholder="What was discussed? Outcome?"
                      style={Object.assign({}, st.input, { minHeight: '70px', resize: 'vertical' })}
                      autoFocus
                    />
                    <div style={{ display: 'flex', gap: '6px', marginTop: '8px', justifyContent: 'flex-end' }}>
                      <button type="button" onClick={function() { setNotesEdit(null) }} style={st.ghostBtn}>CANCEL</button>
                      <button type="button" onClick={saveNotes} disabled={busyId === q.id} style={st.primaryBtn}>{busyId === q.id ? 'SAVING\u2026' : 'SAVE NOTES'}</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    {q.status !== 'contacted' && q.status !== 'resolved' && (
                      <button type="button" onClick={function() { markCalled(q) }} disabled={busyId === q.id} style={st.actionBtn}>
                        {String.fromCodePoint(0x2713)} CALLED
                      </button>
                    )}
                    {q.status !== 'resolved' && (
                      <button type="button" onClick={function() { markResolved(q) }} disabled={busyId === q.id} style={st.resolveBtn}>
                        RESOLVE
                      </button>
                    )}
                    <button type="button" onClick={function() { setNotesEdit({ id: q.id, draft: q.owner_notes || '' }) }} disabled={busyId === q.id} style={st.actionBtn}>
                      {q.owner_notes ? 'EDIT NOTES' : 'ADD NOTES'}
                    </button>
                    <button type="button" onClick={function() { goToBid(q) }} disabled={busyId === q.id} style={st.actionBtn}>
                      CREATE BID
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
      <style>{'@keyframes qq-pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(224,92,92,0.45) } 50% { box-shadow: 0 0 0 6px rgba(224,92,92,0) } }'}</style>
    </div>
  )
}

var st = {
  container: { fontFamily: "'Barlow',sans-serif", maxWidth: '900px' },
  header: { marginBottom: '24px' },
  title: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '28px', fontWeight: 700, letterSpacing: '3px', color: '#ffffff' },
  sub: { fontSize: '14px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' },
  chip: { padding: '6px 12px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer' },
  chipActive: { backgroundColor: 'rgba(180,140,60,0.15)', borderColor: 'rgba(180,140,60,0.4)', color: '#b48c3c' },
  empty: { padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '14px' },
  card: { padding: '16px 20px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px' },
  cardUrgent: { backgroundColor: 'rgba(224,92,92,0.06)', border: '1px solid rgba(224,92,92,0.35)', animation: 'qq-pulse 2s ease-out infinite' },
  urgentBadge: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '2px', color: '#e05c5c', backgroundColor: 'rgba(224,92,92,0.15)', padding: '3px 8px', borderRadius: '3px' },
  contactLink: { color: '#b48c3c', textDecoration: 'none', fontSize: '14px', fontWeight: 600 },
  questionBox: { marginTop: '10px', padding: '10px 12px', backgroundColor: 'rgba(180,140,60,0.06)', border: '1px solid rgba(180,140,60,0.2)', borderRadius: '3px', fontSize: '13px', color: 'rgba(255,255,255,0.75)', fontStyle: 'italic', lineHeight: 1.5 },
  notesBox: { marginTop: '10px', padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '3px' },
  notesLabel: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '9px', fontWeight: 700, letterSpacing: '2px', color: 'rgba(255,255,255,0.35)', marginBottom: '4px' },
  statusBadge: { padding: '4px 10px', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '3px', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px' },
  actionBtn: { padding: '8px 14px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '3px', color: 'rgba(255,255,255,0.7)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer' },
  resolveBtn: { padding: '8px 14px', backgroundColor: 'rgba(74,140,106,0.15)', border: '1px solid rgba(74,140,106,0.35)', borderRadius: '3px', color: '#4a8c6a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },
  ghostBtn: { padding: '8px 14px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '3px', color: 'rgba(255,255,255,0.55)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer' },
  primaryBtn: { padding: '8px 14px', backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', color: '#0a0f1a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },
  input: { width: '100%', padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#ffffff', fontSize: '13px', fontFamily: "'Barlow',sans-serif", outline: 'none', boxSizing: 'border-box' },
}
